import React, { useMemo, useState } from 'react';
import SearchSelect from '../pages/SearchSelect';
import useTrialFilterOptions from '../hooks/useTrialFilterOptions';
import { hasActiveFilters } from '../utils/trialSearchFilters';
import SearchPanel, { SearchField as Field } from './search/SearchPanel';
import { SEARCH_GRID_CLASS as gridClass, SEARCH_INPUT_CLASS as inputClass } from './search/searchStyles';
import { CO_LABEL } from '../utils/contractLabel';

// Label getters are module-level so SearchSelect's option list isn't rebuilt on every render
const getId = o => o?.id;
const getName = o => o?.name ?? '';
const getCustomerNumber = o => (o?.customerid != null ? String(o.customerid) : '');
const getHostLabel = o => (o?.address ? `${o.name} – ${o.address}` : o?.name ?? '');

/**
 * Search panel for the trial overview. Controlled: `filters` holds string values
 * (see utils/trialSearchFilters), `onChange(patch)` receives the changed keys.
 */
// Field order in the full panel; in compact mode the PRIMARY fields are shown and the rest fold out
const ALL_FIELDS = ['q', 'trial', 'location', 'contract_no', 'year', 'customer_no', 'customer_name', 'country', 'host', 'crop', 'study_type', 'trial_type', 'sd', 'pi', 'ft'];
const PRIMARY_FIELDS = ['q', 'year', 'customer_name'];
const SECONDARY_FIELDS = ALL_FIELDS.filter(f => !PRIMARY_FIELDS.includes(f));
// Filter keys only reachable behind "More filters" (customer_id is also set from the visible Customer Name)
const SECONDARY_FILTER_KEYS = ['trial', 'location', 'contract_no', 'country_id', 'host_id', 'crop_id', 'study_type_id', 'trial_type_id', 'sd_id', 'pi_id', 'ft_id'];

// `extra`: more fields shown under the trial filters (e.g. the dashboard's task filters);
// `resetDisabled` overrides when "Reset filters" is off (when `extra` has filters of its own)
export default function TrialSearchFilters({ filters, onChange, onReset, compact = false, extra = null, resetDisabled }) {
  const { options, error } = useTrialFilterOptions();
  const hiddenActiveCount = SECONDARY_FILTER_KEYS.filter(k => String(filters[k] ?? '').trim() !== '').length;
  // Start expanded if filters behind the toggle are already set, so nothing active is hidden
  const [expanded, setExpanded] = useState(() => hiddenActiveCount > 0);
  const customersByNumber = useMemo(
    () => [...options.customers].sort((a, b) => Number(a.customerid) - Number(b.customerid)),
    [options.customers]
  );

  const text = (key) => ({
    value: filters[key],
    onChange: e => onChange({ [key]: e.target.value }),
  });

  const combobox = (key, items, getOptionLabel, placeholder) => (
    <SearchSelect
      options={items}
      value={filters[key] || null}
      onChange={opt => onChange({ [key]: opt ? String(opt.id) : '' })}
      getOptionLabel={getOptionLabel}
      getOptionValue={getId}
      placeholder={placeholder}
      className="text-sm"
    />
  );

  // Several values in one string filter: "1,3" <-> ['1', '3']
  const multiCombobox = (key, items, placeholder) => (
    <SearchSelect
      isMulti
      options={items}
      value={filters[key] ? filters[key].split(',') : []}
      onChange={selected => onChange({ [key]: selected.map(v => String(v?.id ?? v)).join(',') })}
      getOptionLabel={getName}
      getOptionValue={getId}
      placeholder={placeholder}
      className="text-sm"
    />
  );

  const fields = {
    q: (
      <Field key="q" label="Search" className={compact ? 'sm:col-span-2' : 'sm:col-span-2 lg:col-span-4'}>
        <input
          type="search"
          placeholder={`Trial no., customer, host, ${CO_LABEL}...`}
          title={`Searches trial no., customer, host, address, crop, test item, project and ${CO_LABEL}`}
          className={inputClass}
          {...text('q')}
        />
      </Field>
    ),
    trial: (
      <Field key="trial" label="Trial">
        <input type="text" placeholder="Trial no." className={inputClass} {...text('trial')} />
      </Field>
    ),
    location: (
      <Field key="location" label="Location">
        <input type="text" placeholder="Trial address" className={inputClass} {...text('location')} />
      </Field>
    ),
    contract_no: (
      <Field key="contract_no" label={CO_LABEL}>
        <input type="text" inputMode="numeric" placeholder={CO_LABEL} className={inputClass} {...text('contract_no')} />
      </Field>
    ),
    year: (
      <Field key="year" label="Year">
        <select
          value={filters.year}
          onChange={e => onChange({ year: e.target.value })}
          className={inputClass}
        >
          <option value="">All years</option>
          {options.years.map(year => <option key={year} value={String(year)}>{year}</option>)}
        </select>
      </Field>
    ),
    // Customer number and name are two views of the same customer_id
    customer_no: (
      <Field key="customer_no" label="Customer No">
        {combobox('customer_id', customersByNumber, getCustomerNumber, 'Customer no.')}
      </Field>
    ),
    customer_name: (
      <Field key="customer_name" label="Customer Name">
        {combobox('customer_id', options.customers, getName, 'Customer name')}
      </Field>
    ),
    country: <Field key="country" label="Country">{combobox('country_id', options.countries, getName, 'Country')}</Field>,
    host: <Field key="host" label="Host">{combobox('host_id', options.hosts, getHostLabel, 'Host')}</Field>,
    crop: <Field key="crop" label="Crop">{combobox('crop_id', options.crops, getName, 'Crop')}</Field>,
    study_type: <Field key="study_type" label="Study Type">{combobox('study_type_id', options.study_types, getName, 'Study type')}</Field>,
    // Trial Type is TIS's "Area" (GLP, GEP, ...); any of the chosen types
    trial_type: <Field key="trial_type" label="Trial Type" group>{multiCombobox('trial_type_id', options.trial_types, 'Trial type')}</Field>,
    sd: <Field key="sd" label="Study Director">{combobox('sd_id', options.users, getName, 'Study director')}</Field>,
    pi: <Field key="pi" label="Principal Investigator">{combobox('pi_id', options.users, getName, 'Principal investigator')}</Field>,
    ft: <Field key="ft" label="Field Technician">{combobox('ft_id', options.users, getName, 'Field technician')}</Field>,
  };

  return (
    <SearchPanel
      onReset={onReset}
      resetDisabled={resetDisabled ?? !hasActiveFilters(filters)}
      footerStart={compact ? (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            aria-expanded={expanded}
            aria-controls="trial-search-more-filters"
            className="text-sm font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
          >
            {expanded ? 'Fewer filters' : 'More filters'}
            {!expanded && hiddenActiveCount > 0 && ` (${hiddenActiveCount} active)`}
          </button>
          <span className="text-xs text-red-600 dark:text-red-400">{error}</span>
        </div>
      ) : (
        <span className="text-xs text-red-600 dark:text-red-400">{error}</span>
      )}
    >
      {compact ? (
        <>
          <div className={gridClass}>{PRIMARY_FIELDS.map(f => fields[f])}</div>
          {expanded && (
            <div id="trial-search-more-filters" className={`mt-3 ${gridClass}`}>{SECONDARY_FIELDS.map(f => fields[f])}</div>
          )}
        </>
      ) : (
        <div className={gridClass}>{ALL_FIELDS.map(f => fields[f])}</div>
      )}
      {extra}
    </SearchPanel>
  );
}
